import { grammarIsNew } from '../lib/grammarNotes.ts';
import { lessonNo, type LessonV2 } from '../lib/levels.ts';
import { recordOutcome } from '../lib/outcomes.ts';
import { planCourseReview } from '../lib/courseReview.ts';
import { applyCourseSession, applyReviewSession } from '../lib/progress.ts';
import { HOME_ROUTE, LOCAL_PROFILE } from '../lib/route.ts';
import type { RunKey } from '../lib/runSnapshot.ts';
import { buildCourseSession, buildReviewSession } from '../lib/sessionV2.ts';
import type { Outcome, Task } from '../types.ts';
import { renderBanter } from './banterTask.ts';
import { renderBuild } from './buildTask.ts';
import { renderCloze } from './clozeTask.ts';
import { renderGrammar } from './grammarTask.ts';
import { renderQuiz } from './quizTask.ts';
import { renderSay } from './sayTask.ts';
import { renderLangToggle } from './langToggle.ts';
import type { AppCtx, TaskEnv, TaskView } from './ctx.ts';
import { button, h } from './dom.ts';
import { genderOf } from './forms.ts';
import { tr, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { icon, warmStepPoses } from './kit.ts';
import { warmHeads } from './dialogue.ts';
import { renderSpeak } from './speakTask.ts';
import { renderSummary } from './summary.ts';

// Napisy paska lekcji (patch-33): idą za przełącznikiem EN/PL i przepisują się w miejscu — lekcja ma stan i nie rysuje się od nowa.
const T = {
  reviewDay: { en: 'Review of the day', pl: 'Powtórka dnia' },
  finish: { en: 'Finish', pl: 'Zakończ' },
  confirmExit: { en: 'Finish the lesson? Your progress from this session will not be saved.', pl: 'Zakończyć lekcję? Postęp z tej sesji nie zostanie zapisany.' },
  confirmExitReview: { en: 'Finish the review? Your progress from this session will not be saved.', pl: 'Zakończyć powtórkę? Postęp z tej sesji nie zostanie zapisany.' },
  back: { en: 'Back', pl: 'Wstecz' },
  forward: { en: 'Forward', pl: 'Do przodu' },
} as const satisfies Record<string, Pair>;

function renderTask(task: Task, env: TaskEnv): TaskView {
  switch (task.kind) {
    case 'banter':
      return renderBanter(task, env);
    case 'speak':
      return renderSpeak(task, env);
    case 'cloze':
      return renderCloze(task, env);
    case 'build':
      return renderBuild(task, env);
    case 'quiz':
      return renderQuiz(task, env);
    case 'say':
      return renderSay(task, env);
    case 'grammar':
      return renderGrammar(task, env);
  }
}

/** Zadania, w których dziecko coś robi (mówi, wpisuje, układa, wybiera): koralowy pasek postępu; reszta (pogadanka, karta słowa, karta gramatyczna) jest bierna. */
const ACTIVE_KINDS: ReadonlySet<Task['kind']> = new Set(['speak', 'cloze', 'build', 'quiz', 'say']);

/** Co odróżnia lekcję kursu od „Powtórki dnia”; reszta odtwarzacza jest wspólna. */
interface RunSpec {
  tasks: Task[];
  /** Wznowienie po odświeżeniu (patch-34): ten sam profil, dzień i lekcja wracają na zadanie, na którym dziecko było, z dotychczasowymi wynikami. */
  key: RunKey;
  /** Tytuł na pasku w danym języku. */
  title(lang: 'en' | 'pl'): string;
  /** Powtórka dnia: po cofnięciu zostaje PIERWSZA odpowiedź (nie ma gwiazdek, a pierwsze spotkanie ze słowem to miara dla pudełek); lekcja: lepszy wynik mówienia. */
  keepFirstResult?: boolean;
  /** Pytanie przy ✕ (domyślnie o lekcji; „Powtórka dnia” pyta o powtórkę). */
  confirmExit?: Pair;
  /** Zapisuje postęp po ostatnim zadaniu i zwraca ekran podsumowania. */
  finish(outcomes: Outcome[], today: string): HTMLElement;
}

/**
 * Lekcja kursu v2 (SPEC §23.14): kroki 1–6 jednej `LessonV2` z `buildCourseSession`. Ziarno losowania = id lekcji i data, więc w ciągu dnia lista zadań jest ta sama
 * (wznowienie po odświeżeniu działa), a następnego dnia ćwiczenia kroków 5–6 wypadają inaczej. Bez bota w zadaniach (warstwa AI wstrzymana, SPEC §23.27.4).
 */
export function runCourse(ctx: AppCtx, lesson: LessonV2): void {
  const today = ctx.today();
  const tasks = buildCourseSession({ lesson, banter: ctx.banter, ...(ctx.course && grammarIsNew(lesson, ctx.course.lessons, ctx.getProgress().course?.done ?? {}) ? { notes: ctx.course.notes } : {}), gender: genderOf(ctx), seed: `${lesson.id}|${today}` });
  const texts = new Map<string, string>();
  for (const t of tasks) {
    if (t.kind === 'speak') texts.set(t.sentenceId, t.text);
    else if (t.kind === 'say' && t.accept[0]) texts.set(t.word, t.accept[0]);
  }
  runTasks(ctx, {
    tasks,
    key: { profile: ctx.account?.profile()?.id ?? LOCAL_PROFILE, date: today, mode: 'lesson', day: null, lesson: lesson.id },
    title: (lang) => { const no = lessonNo(ctx.course?.lessons ?? [], lesson); return lang === 'en' ? `Lesson ${no} · ${lesson.theme}` : `Lekcja ${no} · ${lesson.themePl}`; },
    finish: (outcomes, day) => {
      const before = ctx.getProgress();
      const after = applyCourseSession(before, { lesson, outcomes, today: day });
      ctx.setProgress(after);
      return renderSummary({ ctx, mode: 'lesson', course: { words: lesson.targets.map((t) => t.word), textOf: (id) => texts.get(id) }, outcomes, before, after });
    },
  });
}

/**
 * „Powtórka dnia” kursu v2 (patch-47, SPEC §23.27): jedna porcja (do 10 słów) zaległych słów kursu, wybrana przez `planCourseReview`, z ćwiczeniem dobranym do pudełka słowa
 * (`buildReviewSession`). Wybór i ziarno zależą od profilu i daty, a postęp zapisuje się dopiero na końcu, więc odświeżenie w trakcie wraca na to samo zadanie. Gdy nic
 * nie jest zaległe (np. po odświeżeniu z innego urządzenia, które już zrobiło porcję), wracamy na ekran główny. Powtórka nie daje gwiazdek ani nie rusza serii.
 */
export function runReview(ctx: AppCtx): void {
  const course = ctx.course;
  if (!course) return ctx.goHome();
  const today = ctx.today();
  const before = ctx.getProgress();
  const profile = ctx.account?.profile()?.id ?? LOCAL_PROFILE;
  const plan = planCourseReview({ progress: before, lessons: course.lessons, today, profile });
  const items = before.course?.items ?? {};
  const picks = plan.words.map((word) => ({ word, box: items[word]!.box }));
  const tasks = buildReviewSession({ picks, lessons: course.lessons, done: before.course?.done ?? {}, gender: genderOf(ctx), seed: `${profile}|${today}|review` });
  if (tasks.length === 0) return ctx.goHome();
  runTasks(ctx, {
    tasks,
    key: { profile, date: today, mode: 'review', day: null, lesson: 'review' },
    title: (lang) => T.reviewDay[lang],
    confirmExit: T.confirmExitReview,
    keepFirstResult: true,
    finish: (outcomes, day) => {
      const current = ctx.getProgress();
      const after = applyReviewSession(current, { outcomes, today: day });
      ctx.setProgress(after);
      // gwiazdki za mówienie w powtórce nie liczą się do wyniku, więc podsumowanie ich nie pokazuje (ani sumy „x / y gwiazdek”)
      const shown = outcomes.map(({ stars: _stars, percent: _percent, ...rest }): Outcome => rest);
      const answered = outcomes.filter((o) => o.kind === 'quiz' || o.kind === 'cloze' || o.kind === 'say');
      return renderSummary({ ctx, mode: 'review', outcomes: shown, before: current, after, review: { total: answered.length, correct: answered.filter((o) => o.correct === true).length } });
    },
  });
}

function runTasks(ctx: AppCtx, spec: RunSpec): void {
  const { tasks, key } = spec;
  warmStepPoses();
  warmHeads();
  const today = ctx.today();
  const taskIds = tasks.map((t) => t.id);
  const saved = ctx.run?.resume(key, taskIds) ?? null;
  const outcomes: Outcome[] = saved ? [...saved.outcomes] : [];
  let index = saved ? saved.index : 0;
  /** Ostatni nowy krok: pierwsze nieukończone zadanie (patch-49). „←” cofa dziecko za ten punkt, „→” prowadzi do niego ekran po ekranie; dalej się nie da. */
  let reached = saved ? saved.reached : 0;
  const remember = (): void => ctx.run?.save(key, taskIds, { index, outcomes, reached });
  /** Poprzedni ekran lekcji (każdy, także pogadanka — „wstecz” wraca dokładnie o jeden ekran); null = jesteśmy na pierwszym. */
  const previous = (): number | null => (index > 0 ? index - 1 : null);
  let view: TaskView | null = null;

  const bar = h('div', { class: 'bar-fill', style: 'width:0%' });
  const title = spec.title;
  const body = h('main', { class: 'stage lesson-stage' });
  const leave = (): void => {
    ctx.run?.clear(); // świadome wyjście kończy lekcję bez zapisu — następny start zaczyna od początku
    view?.destroy?.();
    ctx.player.stop();
    ctx.goHome();
  };
  const exit = txAttr(ctx, button(icon('close'), () => {
    if (confirm(tr(ctx, spec.confirmExit ?? T.confirmExit))) leave();
  }, { class: 'icon-btn', testid: 'exit' }), 'title', T.finish);

  // ← wraca o jeden ekran (ponownie pokazuje go od początku); wynik zadania, które już raz zaliczono, nie liczy się drugi raz.
  // Na pierwszym ekranie nie ma dokąd wracać w lekcji, więc ← wraca na ekran główny (nic jeszcze nie zrobiono, więc bez pytania „Przerwać?”) — przycisk działa zawsze.
  const goBack = (): void => {
    const j = previous();
    if (j === null) return leave();
    index = j;
    remember();
    show();
  };
  const back = button(icon('back'), goBack, { class: 'icon-btn', testid: 'back' });
  txAttr(ctx, back, 'title', T.back);
  txAttr(ctx, back, 'aria-label', T.back);

  // → przesuwa o JEDEN ekran do przodu (tak samo jak ← cofa o jeden), a zatrzymuje się na ostatnim NOWYM kroku — nieukończonego zadania nie da się przeskoczyć, a po
  // cofnięciu o kilka ekranów dziecko wraca do niego po kolei, nie jednym skokiem (poprawka z 19.10, 12:20). Na tym kroku przycisk jest niewidoczny, ale zajmuje
  // swoje miejsce, więc pasek się nie przesuwa.
  const goForward = (): void => {
    if (index >= reached) return;
    index++;
    remember();
    show();
  };
  const forward = button(icon('arrow'), goForward, { class: 'icon-btn is-off', testid: 'forward', disabled: true });
  txAttr(ctx, forward, 'title', T.forward);
  txAttr(ctx, forward, 'aria-label', T.forward);
  const syncForward = (): void => {
    const behind = index < reached;
    forward.classList.toggle('is-off', !behind);
    forward.disabled = !behind;
  };

  // pasek: ✕ ← | tytuł nad paskiem postępu | EN ⌄. Pasek postępu jest koralowy w zadaniach, w których dziecko coś robi (mówi, wpisuje, układa), a fioletowy w biernych (pogadanka, karta słowa, podsumowanie).
  const topbar = h('header', { class: 'topbar lesson-top', 'data-kind': 'passive', 'data-testid': 'topbar' }, h('div', { class: 'tb-left' }, exit, back, forward), h('div', { class: 'tb-mid' }, txText(ctx, h('div', { class: 'title', 'data-testid': 'title' }), title), h('div', { class: 'bar', 'aria-hidden': 'true' }, bar)), renderLangToggle(ctx));
  ctx.root.replaceChildren(topbar, body);

  function show(): void {
    view?.destroy?.();
    view = null;
    ctx.player.stop();
    if (index >= tasks.length) return finish();
    back.hidden = false;
    syncForward();
    const task = tasks[index]!;
    bar.style.width = `${Math.round((index / tasks.length) * 100)}%`;
    topbar.dataset.kind = ACTIVE_KINDS.has(task.kind) ? 'active' : 'passive';
    let called = false;
    const env: TaskEnv = {
      ctx,
      ...(previous() !== null ? { back: goBack } : {}),
      done: (outcome) => {
        if (called) return; // ochrona przed podwójnym kliknięciem
        called = true;
        if (outcome) recordOutcome(outcomes, outcome, spec.keepFirstResult === true); // zadanie zrobione drugi raz (po „←”): mówienie — lepszy z dwóch wyników (nigdy gorszy), reszta — pierwszy
        index++;
        if (index > reached) reached = index; // zrobiony był ostatni nowy krok → następny jest nowym ostatnim
        remember();
        show();
      },
    };
    view = renderTask(task, env);
    body.replaceChildren(view.el);
    body.scrollTop = 0;
    window.scrollTo(0, 0);
  }

  function finish(): void {
    ctx.run?.clear(); // postęp zapisuje się niżej, więc po odświeżeniu na podsumowaniu wracamy na ekran główny, a nie do lekcji
    ctx.at?.(HOME_ROUTE);
    back.hidden = true;
    forward.classList.add('is-off');
    forward.disabled = true;
    bar.style.width = '100%';
    topbar.dataset.kind = 'passive';
    body.replaceChildren(spec.finish(outcomes, today));
  }

  show();
}
